<template>
  <AppNavbar />
  <div class="vx-admin">

    <div class="d-flex align-items-end justify-content-between mb-3">
      <div>
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span><span>Cobros</span>
        </div>
        <h1 class="vx-titulo">Cobros</h1>
      </div>
      <router-link v-if="puedeCobrar" class="vx-btn vx-btn-primario" :to="{ name: 'Cobranza' }">
        <i class="bi bi-cash-stack"></i> Cobrar
      </router-link>
    </div>

    <!-- Los que necesitan a alguien van arriba y con nombre propio. Mezclarlos
         con los que se reenvían solos haría que el operador revise cobros que
         no tienen nada que revisar. -->
    <div v-if="requierenAtencion.length" class="vx-panel mb-3" data-test="panel-atencion"
         style="border-color: rgba(191,122,0,0.35)">
      <div class="vx-panel-cabecera">
        <div>
          <h2 class="vx-panel-titulo">Necesitan tu atención</h2>
          <div class="vx-ayuda mt-1">
            Estos cobros no van a resolverse solos: el documento no se emitió y no se reintenta.
          </div>
        </div>
        <span class="vx-estado vx-estado-atencion vx-num">{{ requierenAtencion.length }}</span>
      </div>

      <div
        v-for="c in requierenAtencion" :key="c.cr_cobro_id"
        class="d-flex align-items-center gap-3 px-3 py-2 border-bottom"
      >
        <div class="flex-grow-1 min-w-0">
          <div style="font-size: 13.5px; font-weight: 570">
            {{ c.cliente_nombre }} · {{ formatearMonto(c.monto_total) }}
          </div>
          <div class="vx-celda-sub">
            {{ estadoEmision(c).texto }} · {{ c.ultimo_error || 'sin detalle' }}
          </div>
        </div>
        <button
          v-if="puedeCobrar"
          class="vx-btn vx-btn-sm"
          :data-test="`completar-${c.cr_cobro_id}`"
          :disabled="completando === c.cr_cobro_id"
          @click="completar(c)"
        >
          <span v-if="completando === c.cr_cobro_id" class="spinner-border spinner-border-sm"></span>
          Completar emisión
        </button>
      </div>
    </div>

    <div class="vx-panel">
      <div class="d-flex align-items-center flex-wrap gap-2 p-3 border-bottom">
        <!-- Sin rango de fechas el listado es «los últimos 200 cobros», que no
             contesta «¿qué se cobró el martes?». -->
        <label class="vx-ayuda" for="cobros-desde">Desde</label>
        <input id="cobros-desde" v-model="filtroDesde" type="date" class="vx-campo vx-campo-sm"
               style="width: auto" @change="cargar">
        <label class="vx-ayuda" for="cobros-hasta">Hasta</label>
        <input id="cobros-hasta" v-model="filtroHasta" type="date" class="vx-campo vx-campo-sm"
               style="width: auto" @change="cargar">
        <select v-model="filtroEstado" class="vx-campo vx-campo-sm" style="width: auto"
                aria-label="Estado de emisión" @change="cargar">
          <option value="">Todos los estados</option>
          <option value="EMITIDO">Emitidos</option>
          <option value="PENDIENTE_EMISION">En cola de reenvío</option>
          <option value="FALLIDO">Fallidos</option>
          <option value="NO_REQUERIDO">Sin documento electrónico</option>
          <option value="ANULADO">Anulados</option>
        </select>
        <div class="flex-grow-1"></div>
        <span v-if="!cargando" class="vx-num vx-ayuda">{{ cobros.length }} cobros</span>
      </div>

      <div v-if="cargando" class="vx-vacio">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <span class="vx-vacio-texto">Cargando…</span>
      </div>

      <div v-else-if="error" class="vx-vacio">
        <i class="bi bi-exclamation-triangle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">No se pudo cargar el listado</span>
        <span class="vx-vacio-texto">{{ error }}</span>
        <button class="vx-btn vx-btn-sm mt-1" @click="cargar">Reintentar</button>
      </div>

      <div v-else-if="!cobros.length" class="vx-vacio" data-test="cobros-vacio">
        <i class="bi bi-cash-stack" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">
          {{ filtroEstado ? 'Ningún cobro con ese estado' : 'Todavía no se registró ningún cobro' }}
        </span>
        <span class="vx-vacio-texto">
          Los cobros aparecen acá apenas se registran, con el estado de su documento.
        </span>
      </div>

      <table v-else class="vx-tabla" data-test="tabla-cobros">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Responsable</th>
            <th>Documento</th>
            <th class="vx-col-num">Monto</th>
            <th>Emisión</th>
            <th style="width: 90px"></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="c in cobros" :key="c.cr_cobro_id">
          <tr>
            <td class="vx-num text-nowrap">{{ fechaHora(c.creado_en) }}</td>
            <td>
              <button class="vx-celda-principal btn btn-link p-0 text-start"
                      style="font: inherit; color: inherit; text-decoration: none"
                      :data-test="`detalle-${c.cr_cobro_id}`"
                      @click="alternarDetalle(c)">
                <i class="bi" :class="detalleDe === c.cr_cobro_id ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                {{ c.cliente_nombre }}
              </button>
              <div class="vx-celda-sub vx-num">#{{ c.cr_cobro_id }} · {{ c.forma_pago }}</div>
              <!-- D43 · RN-124. La fila sigue diciendo de QUIÉN es el cobro
                   —el responsable— y agrega quién lo pagó cuando no fue él.
                   Sin esto, dos cobros idénticos en pantalla habrían salido
                   con facturas a nombres distintos y nada lo explicaría. -->
              <div v-if="c.pagador_nombre" class="vx-celda-sub"
                   :data-test="`pagador-${c.cr_cobro_id}`">
                <i class="bi bi-person-check"></i> Pagó {{ c.pagador_nombre }}
              </div>
            </td>
            <td>{{ c.documento_tipo === 'RECIBO' ? 'Recibo' : 'Factura' }}</td>
            <td class="vx-col-num vx-num">{{ formatearMonto(c.monto_total) }}</td>
            <td>
              <span class="vx-estado" :class="estadoEmision(c).clase">{{ estadoEmision(c).texto }}</span>
              <!--
                Qué pasó con la FACTURA, debajo del estado. «Anulado» a secas no
                respondía la única pregunta que importa cuando viene el
                contador: si el documento sigue vivo en SIFEN.

                Sólo en los anulados, a propósito: en los demás la nota repite
                lo que el estado ya dice, y una columna con una frase en cada
                fila deja de leerse.
              -->
              <div v-if="c.estado_emision === 'ANULADO'"
                   class="vx-celda-sub"
                   :data-test="`resolucion-fiscal-${c.cr_cobro_id}`">
                {{ textoDeResolucionFiscal(c) }}
              </div>
            </td>
            <td class="text-end">
              <!--
                Un menú por fila en vez de dos botones sueltos. Las acciones de
                un cobro crecieron —comprobante, detalle, completar emisión,
                anular— y ponerlas todas a la vista hace una columna que ocupa
                media tabla y donde «Anular» queda al lado de lo que se usa
                todos los días.

                E10 (CR-E10-006): «Entregar comprobante» es el que cierra H-AB.
                Acá es adonde vuelve el operador cuando el pagador le pide el
                comprobante otra vez, días después.
              -->
              <div class="dropdown" :data-menu-cobro="c.cr_cobro_id">
                <button class="vx-btn vx-btn-sm" type="button"
                        :aria-expanded="menuAbierto === c.cr_cobro_id"
                        :aria-label="`Acciones del cobro ${c.cr_cobro_id}`"
                        :data-test="`acciones-${c.cr_cobro_id}`"
                        @click="alternarMenu(c)">
                  <span v-if="buscandoComprobante === c.cr_cobro_id"
                        class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-three-dots-vertical"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end"
                    :class="{ show: menuAbierto === c.cr_cobro_id }">
                  <li>
                    <button class="dropdown-item" :data-test="`detalle-menu-${c.cr_cobro_id}`"
                            @click="desdeElMenu(() => alternarDetalle(c))">
                      <i class="bi bi-list-ul me-2"></i>
                      {{ detalleDe === c.cr_cobro_id ? 'Ocultar detalle' : 'Ver detalle' }}
                    </button>
                  </li>
                  <!-- RN-152 (SPEC_NOTAS_DE_CREDITO). También en los ANULADOS: el
                       documento anulado se entrega marcado, y si la anulación
                       emitió una nota de crédito —o la constancia del recibo—
                       es lo primero que se ofrece. Antes el menú lo escondía y
                       el operador no tenía de dónde sacarla (H-AK). -->
                  <li v-if="c.estado_emision === 'EMITIDO' || c.estado_emision === 'ANULADO'">
                    <button class="dropdown-item" :data-test="`comprobante-${c.cr_cobro_id}`"
                            @click="desdeElMenu(() => entregarComprobante(c))">
                      <i class="bi bi-box-arrow-up-right me-2"></i>
                      {{ c.estado_emision === 'ANULADO' ? 'Entregar documentos de la anulación' : 'Entregar comprobante' }}
                    </button>
                  </li>
                  <!-- Sólo cuando hace falta: un cobro que se resuelve solo no
                       tiene que ofrecer un botón que sugiere lo contrario. -->
                  <li v-if="puedeCobrar && c.requiere_atencion">
                    <button class="dropdown-item" :data-test="`completar-menu-${c.cr_cobro_id}`"
                            :disabled="completando === c.cr_cobro_id"
                            @click="desdeElMenu(() => completar(c))">
                      <i class="bi bi-arrow-repeat me-2"></i>Completar emisión
                    </button>
                  </li>
                  <template v-if="puedeAnular && c.estado_emision !== 'ANULADO'">
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <button class="dropdown-item text-danger" :data-test="`anular-${c.cr_cobro_id}`"
                              @click="desdeElMenu(() => pedirAnulacion(c))">
                        <i class="bi bi-x-circle me-2"></i>Anular cobro
                      </button>
                    </li>
                  </template>
                </ul>
              </div>
            </td>
          </tr>

          <!-- Qué se cobró. Vive en la misma tabla y no en otra pantalla: el
               operador que abre un cobro de hace tres días quiere ver las
               cuotas y los productos ahí mismo, no navegar. -->
          <tr v-if="detalleDe === c.cr_cobro_id">
            <td colspan="6" style="background: var(--vx-fondo-2)">
              <div v-if="cargandoDetalle" class="vx-ayuda">Cargando el detalle…</div>
              <div v-else-if="errorDetalle" class="vx-error">{{ errorDetalle }}</div>
              <div v-else-if="detalle" class="d-flex flex-wrap gap-4" :data-test="`panel-detalle-${c.cr_cobro_id}`">
                <div v-if="detalle.aplicaciones?.length">
                  <div class="vx-ayuda mb-1">{{ vocabulario.plural }}</div>
                  <div v-for="a in detalle.aplicaciones" :key="a.cr_aplicacion_id"
                       class="vx-num" style="font-size: 13px">
                    {{ a.periodo }} · {{ a.descripcion }} —
                    <strong>{{ formatearMonto(a.monto_aplicado) }}</strong>
                    <span v-if="a.revertida" class="vx-estado vx-estado-atencion ms-1">Revertida</span>
                  </div>
                </div>
                <div v-if="detalle.productos?.length">
                  <div class="vx-ayuda mb-1">Productos</div>
                  <div v-for="(p, i) in detalle.productos" :key="i" class="vx-num" style="font-size: 13px">
                    {{ p.cantidad }} × {{ p.nombre_producto || p.descripcion }} —
                    <strong>{{ formatearMonto(p.precio_unitario_bruto * p.cantidad) }}</strong>
                  </div>
                </div>
                <div v-if="!detalle.aplicaciones?.length && !detalle.productos?.length" class="vx-ayuda">
                  Este cobro no tiene {{ vocabulario.plural.toLowerCase() }} ni productos asociados.
                </div>
                <div v-if="detalle.motivo_anulacion">
                  <div class="vx-ayuda mb-1">Anulación</div>
                  <div style="font-size: 13px">{{ detalle.motivo_anulacion }}</div>
                </div>
              </div>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <AbrirCajaModal v-model:abierto="mostrarApertura" @abierta="alAbrirCaja" />

    <ComprobanteEntrega
      :abierto="Boolean(comprobanteDe)"
      :cobro-id="comprobanteDe"
      @cerrado="comprobanteDe = null"
    />

    <!-- Anular pide motivo: la base lo exige (articulo 6) y sin el no tiene
         sentido mandar la petición. -->
    <div v-if="anulando" class="vx-panel mt-3" data-test="panel-anulacion">
      <div class="vx-panel-cabecera">
        <h2 class="vx-panel-titulo">Anular cobro #{{ anulando.cr_cobro_id }}</h2>
      </div>
      <div class="p-3">
        <div class="vx-nota mb-2">
          <i class="bi bi-info-circle"></i>
          <span>
            Se revierten las aplicaciones y las {{ vocabulario.plural.toLowerCase() }} vuelven
            a quedar con su saldo. Queda registrado quién anuló y por qué.
            En tu caja se registra un <strong>egreso</strong> que contrapesa el cobro,
            y si el cobro llevaba productos, el stock vuelve al mostrador.
          </span>
        </div>
        <label class="vx-etiqueta" for="motivo-anulacion">Motivo</label>
        <input
          id="motivo-anulacion" v-model.trim="motivo" class="vx-campo"
          placeholder="Por ejemplo: cobro cargado por error"
          :class="{ 'vx-campo-error': errorAnulacion }"
        >
        <div v-if="errorAnulacion" class="vx-error" data-test="error-anulacion">
          {{ errorAnulacion }}
          <!-- Anular mueve plata, así que ahora exige caja abierta igual que
               cobrar. Sin este botón el operador queda con el mensaje y sin
               salida, que es el problema que ya se corrigió en el cobro. -->
          <div v-if="cajaCerrada && puedeAbrirCaja" class="mt-2">
            <button class="btn btn-success btn-sm" data-test="btn-abrir-caja"
                    @click="mostrarApertura = true">
              <i class="bi bi-unlock me-1"></i>Abrir caja
            </button>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="vx-btn" @click="anulando = null">Cancelar</button>
          <button class="vx-btn vx-btn-primario" data-test="btn-confirmar-anulacion" @click="anular">
            Anular cobro
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Listado de cobros y completado explícito de la emisión — CR-E3-005, CR-E3-008.
// SPEC RN-27, RN-45, RN-46.

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import ComprobanteEntrega from '@/components/ComprobanteEntrega.vue'
import AbrirCajaModal from '@/components/AbrirCajaModal.vue'
import { useNotificationStore } from '@/stores/notifications'
import { hasAnyPermission, hasPermission } from '@/services/authSessionService'
import {
  listarCobros, obtenerCobro, completarEmision, anularCobro, estadoEmision,
  textoDeResolucionFiscal,
  obtenerVocabulario, VOCABULARIO_POR_DEFECTO, formatearMonto, fechaHora
} from '@/services/cobranzaService'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const notificaciones = useNotificationStore()

const cobros = ref([])
const cargando = ref(true)
const error = ref('')
const filtroEstado = ref('')
// Arranca en el mes corriente y no «todo»: el listado sin rango trae los
// ultimos 200 cobros de la historia, que con el tiempo deja de ser util.
const hoy = fechaIsoLocal()
const filtroDesde = ref(`${hoy.slice(0, 7)}-01`)
const filtroHasta = ref(hoy)
const detalleDe = ref(null)
const detalle = ref(null)
const cargandoDetalle = ref(false)
const errorDetalle = ref('')
const completando = ref(null)
const anulando = ref(null)
// El cobro cuyo comprobante se está entregando (E10). Es un id y no un objeto:
// el componente lo pide al backend, que es el único que sabe si hay enlace.
const comprobanteDe = ref(null)
const buscandoComprobante = ref(null)

// Menu de acciones por fila. El estado se maneja aca y NO con
// `data-bs-toggle="dropdown"`: el data-api de dropdowns de Bootstrap no esta
// activo en esta app —ya documentado en `Inventario/ProductosView.vue`, y
// vuelto a verificar el 2026-09-07: con `data-bs-toggle` el boton no hacia
// absolutamente nada—. Los desplegables del navbar abren por CSS
// (`:hover`/`:focus-within`), que es por que parecen funcionar.
//
// Con la clase `show` alcanza para que la hoja de estilos de Bootstrap lo
// muestre; el cierre por clic fuera y por Escape se resuelve aca.
const menuAbierto = ref(null)

function alternarMenu (cobro) {
  menuAbierto.value = menuAbierto.value === cobro.cr_cobro_id ? null : cobro.cr_cobro_id
}

// Toda accion del menu lo cierra antes de ejecutarse: dejar el desplegable
// abierto encima del modal del comprobante es la clase de detalle que hace
// sentir la pantalla rota.
function desdeElMenu (accion) {
  menuAbierto.value = null
  accion()
}

function alClicAfuera (evento) {
  if (menuAbierto.value === null) return
  // El clic sobre el propio menu abierto lo maneja el boton o la accion.
  if (evento.target?.closest?.(`[data-menu-cobro="${menuAbierto.value}"]`)) return
  menuAbierto.value = null
}

function alEscape (evento) {
  if (evento.key === 'Escape') menuAbierto.value = null
}

onMounted(() => {
  document.addEventListener('click', alClicAfuera)
  document.addEventListener('keydown', alEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', alClicAfuera)
  document.removeEventListener('keydown', alEscape)
})
const motivo = ref('')
const errorAnulacion = ref('')
// Anular ahora registra un egreso de caja, así que necesita caja abierta —
// mismo caso que cobrar, misma salida.
const cajaCerrada = ref(false)
const mostrarApertura = ref(false)
const puedeAbrirCaja = hasPermission('abrir_caja')

function alAbrirCaja () {
  cajaCerrada.value = false
  errorAnulacion.value = ''
  notificaciones.notify({ type: 'success', message: 'Caja abierta. Ya podés anular.' })
}

const puedeCobrar = computed(() => hasAnyPermission(['cobrar_deudas']))
const puedeAnular = computed(() => hasAnyPermission(['anular_cobro']))

const etiquetas = ref({ ...VOCABULARIO_POR_DEFECTO })
const vocabulario = computed(() => ({
  singular: etiquetas.value['deuda.singular'],
  plural: etiquetas.value['deuda.plural']
}))

// `requiere_atencion` lo DERIVA el backend: una factura en cola de reenvío se
// emite sola y no entra acá; un fallo funcional y un recibo pendiente sí.
const requierenAtencion = computed(() => cobros.value.filter((c) => c.requiere_atencion))

async function cargar () {
  cargando.value = true
  error.value = ''
  try {
    cobros.value = await listarCobros({
      estado: filtroEstado.value,
      fecha_desde: filtroDesde.value,
      fecha_hasta: filtroHasta.value
    })
  } catch (err) {
    error.value = err?.userMessage || 'Error inesperado al consultar los cobros.'
    cobros.value = []
  } finally {
    cargando.value = false
  }
}

// El detalle se pide al abrirlo y no con el listado: son N cobros en pantalla
// y traer las cuotas de todos para mostrar las de uno es pagar por lo que
// nadie miro.
async function alternarDetalle (cobro) {
  if (detalleDe.value === cobro.cr_cobro_id) {
    detalleDe.value = null
    return
  }
  detalleDe.value = cobro.cr_cobro_id
  detalle.value = null
  errorDetalle.value = ''
  cargandoDetalle.value = true
  try {
    detalle.value = await obtenerCobro(cobro.cr_cobro_id)
  } catch (err) {
    errorDetalle.value = err?.userMessage || 'No se pudo leer el detalle del cobro.'
  } finally {
    cargandoDetalle.value = false
  }
}

async function completar (cobro) {
  completando.value = cobro.cr_cobro_id
  try {
    await completarEmision(cobro.cr_cobro_id)
    notificaciones.notify({ type: 'success', message: 'Emisión completada.' })
    await cargar()
  } catch (err) {
    notificaciones.notify({
      type: 'error',
      message: err?.userMessage || 'No se pudo completar la emisión.'
    })
  } finally {
    completando.value = null
  }
}

function entregarComprobante (cobro) {
  buscandoComprobante.value = cobro.cr_cobro_id
  comprobanteDe.value = cobro.cr_cobro_id
  // El spinner es del botón; el componente muestra el suyo mientras pide. Se
  // apaga en el próximo tick para que el click no quede sin respuesta visible.
  setTimeout(() => { buscandoComprobante.value = null }, 300)
}

function pedirAnulacion (cobro) {
  anulando.value = cobro
  motivo.value = ''
  errorAnulacion.value = ''
}

// Qué se hizo con el documento fiscal, en palabras del mostrador. El operador
// no conoce «CDC», «NCE» ni «evento de cancelación».
// El texto de la resolución fiscal vive en el servicio
// (`textoDeResolucionFiscal`), para que el aviso de ahora y la fila de después
// digan lo mismo. Acá sólo se adapta la forma: al anular llega
// `{ instrumento, referencia }`, y en la fila llega el cobro entero.
function textoDeResolucion (resolucion) {
  return textoDeResolucionFiscal({
    anulacion_instrumento: resolucion?.instrumento,
    anulacion_referencia: resolucion?.referencia
  })
}

async function anular () {
  if (!motivo.value) {
    errorAnulacion.value = 'Indicá el motivo de la anulación.'
    return
  }
  try {
    cajaCerrada.value = false
    const r = await anularCobro(anulando.value.cr_cobro_id, { motivo: motivo.value })
    // CR-AF-007. Se dice QUÉ pasó con el documento fiscal, no sólo que el cobro
    // se anuló: «anulado» a secas dejaba al operador sin saber si la factura
    // sigue viva en SIFEN — que es justo lo que esta etapa vino a resolver.
    notificaciones.notify({
      type: 'success',
      message: 'Cobro anulado y saldos restituidos. Egreso registrado en caja'
        + (r?.productos_devueltos
          ? ` y ${r.productos_devueltos} producto(s) devuelto(s) al stock. `
          : '. ')
        + textoDeResolucion(r?.resolucion_fiscal)
    })
    anulando.value = null
    await cargar()
    // RN-149. La entrega se abre sola: el operador tiene al pagador enfrente, y
    // el aviso de arriba dura segundos. Trae el documento anulado y, si la
    // anulación emitió uno, la nota de crédito o la constancia del recibo.
    if (r?.comprobante_abrir?.id) comprobanteDe.value = r.comprobante_abrir.id
  } catch (err) {
    // El bloqueo de RA-5 no es un error del operador: es «todavía no se puede,
    // volvé a intentar». Se muestra como aviso y se aclara que no se revirtió
    // nada, para que no quede la duda de si el cobro quedó a medias.
    errorAnulacion.value = err?.userMessage || 'No se pudo anular el cobro.'
    cajaCerrada.value = err?.normalized?.situation === 'caja_no_abierta'
  }
}

onMounted(async () => {
  etiquetas.value = await obtenerVocabulario()
  await cargar()
})
</script>

<style scoped>
/* Posición del menú de acciones de cada fila.
 *
 * Bootstrap deja `top`/`right` a cargo de Popper: sus reglas de alineación
 * viven bajo `.dropdown-menu[data-bs-popper]`, y ese atributo lo pone el JS
 * que en esta app no corre. Sin él, `dropdown-menu-end` no hace NADA y el
 * menú se queda en su posición estática —a la derecha del botón—, se sale de
 * la tabla y aparece una barra de scroll horizontal en toda la pantalla.
 * Reportado el 2026-09-07, con la captura del desborde.
 *
 * Se ancla a mano al `.dropdown`, que ya es `position: relative`. Es el mismo
 * arreglo que `Inventario/ProductosView.vue` para su botón partido.
 */
.dropdown-menu {
  top: 100%;
  right: 0;
  left: auto;
  margin-top: 0.25rem;
}
</style>
